/**
 * QR codes: every rule that does not need a database.
 * Spec: docs/superpowers/specs/2026-10-12-website-qr-codes-design.md
 *
 * A code is printed, so everything here leans toward "the scan still works".
 * An unknown, paused or archived code redirects home rather than 404ing, and a
 * scan the CRM cannot resolve is still visible in GA4 through its UTM tags.
 */
import { todayKey, addDaysKey, CENTRAL_TZ } from "./weeks";

export const WEBSITE_ORIGIN = "https://www.firstturncapital.com";
export const WEBSITE_HOME = `${WEBSITE_ORIGIN}/`;
const OWN_HOSTS = new Set(["firstturncapital.com", "www.firstturncapital.com"]);

// No 0/O/o or 1/l/I: a code is occasionally read off paper and typed.
export const CODE_ALPHABET = "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ";
export const CODE_LENGTH = 7;

/**
 * Rejection sampling: 256 is not a multiple of 56, so a plain `b % 56` would
 * make the first 32 characters slightly more likely. Bytes at or above 224
 * (the largest multiple of 56 that fits) are thrown away instead.
 */
export function generateCode(randomBytes: (n: number) => Uint8Array): string {
  const limit = 256 - (256 % CODE_ALPHABET.length);
  let out = "";
  while (out.length < CODE_LENGTH) {
    for (const b of randomBytes(CODE_LENGTH * 2)) {
      if (b < limit) out += CODE_ALPHABET[b % CODE_ALPHABET.length];
      if (out.length === CODE_LENGTH) break;
    }
  }
  return out;
}

export function isCodeShape(s: string): boolean {
  return /^[A-Za-z0-9]{4,16}$/.test(s);
}

export function qrLinkFor(code: string): string {
  return `${WEBSITE_ORIGIN}/q/${code}`;
}

export type QrDevice = "ios" | "android" | "desktop" | "other";

/**
 * Link-preview fetchers, mail scanners, crawlers and scripts. A QR code is
 * mostly scanned by a phone camera, so this matters most when someone pastes
 * the link into a chat and the chat app fetches it for a preview.
 * `\bbot\b` (not a bare "bot") so the Cubot phone brand is not a bot.
 */
const BOT_UA =
  /\+https?:\/\/|\bbot\b|crawler|spider|googlebot|bingbot|applebot|slackbot|twitterbot|linkedinbot|discordbot|telegrambot|facebookexternalhit|facebot|whatsapp|skypeuripreview|embedly|proofpoint|mimecast|barracuda|safelinks|headless|python-requests|python-urllib|curl\/|wget\/|go-http-client|okhttp|node-fetch|undici|axios\//i;

export function classifyUserAgent(ua: string | null | undefined): { device: QrDevice; isBot: boolean } {
  const s = (ua ?? "").trim();
  // A real phone always sends a user agent.
  if (!s) return { device: "other", isBot: true };
  const device: QrDevice = /iPhone|iPad|iPod/.test(s)
    ? "ios"
    : /Android/.test(s)
      ? "android"
      : /Windows NT|Macintosh|X11|CrOS|Linux x86_64/.test(s)
        ? "desktop"
        : "other";
  return { device, isBot: BOT_UA.test(s) };
}

const MAX_URL = 2000;

export function normalizeDestination(
  raw: string | null | undefined,
): { ok: true; url: string } | { ok: false; error: string } {
  let s = (raw ?? "").trim();
  if (!s) return { ok: true, url: WEBSITE_HOME };
  if (s.length > MAX_URL) return { ok: false, error: "That link is too long." };
  // "firstturncapital.com/faq" typed without a scheme.
  if (!s.startsWith("/") && !/^[a-z][a-z0-9+.-]*:/i.test(s) && /^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(s)) {
    s = `https://${s}`;
  }
  let u: URL;
  try {
    u = s.startsWith("/") ? new URL(s, WEBSITE_ORIGIN) : new URL(s);
  } catch {
    return { ok: false, error: "Enter a full link (https://…) or a page path like /contact." };
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") {
    return { ok: false, error: "Links must start with https://" };
  }
  if (OWN_HOSTS.has(u.hostname.toLowerCase())) {
    u.protocol = "https:";
    u.hostname = "www.firstturncapital.com";
  }
  return { ok: true, url: u.toString() };
}

/**
 * Tags our own site only, and only when the link carries no utm_ tag already:
 * a hand-tagged link keeps its attribution whole rather than half ours.
 */
export function withUtm(destination: string, tags: { campaign: string; code: string }): string {
  let u: URL;
  try {
    u = new URL(destination);
  } catch {
    return destination;
  }
  if (!OWN_HOSTS.has(u.hostname.toLowerCase())) return destination;
  for (const k of u.searchParams.keys()) if (k.toLowerCase().startsWith("utm_")) return destination;
  u.searchParams.set("utm_source", "qr");
  u.searchParams.set("utm_medium", "offline");
  u.searchParams.set("utm_campaign", tags.campaign);
  u.searchParams.set("utm_content", tags.code);
  return u.toString();
}

/** Stored at creation and never re-derived, so a rename cannot split GA4 history. */
export function slugifyCampaign(name: string): string {
  const slug = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return slug || "qr";
}

export interface ScanTarget {
  destination_url: string;
  utm_campaign: string;
  paused_at: unknown;
  archived_at: unknown;
}

/** Where a scan goes. Never a 404: a printed code must always land somewhere. */
export function scanRedirectUrl(code: string, row: ScanTarget | null): string {
  if (!row) return withUtm(WEBSITE_HOME, { campaign: "unknown-code", code });
  const live = !row.paused_at && !row.archived_at;
  return withUtm(live ? row.destination_url : WEBSITE_HOME, { campaign: row.utm_campaign, code });
}

/**
 * Whether a scan of a per-person code should ping the code's creator.
 * `previousScanAt` is the latest earlier non-bot scan of the same code.
 * Once per Central day: a second scan the same day is the same moment of
 * interest, and a ping for each would turn a good signal into noise.
 */
export function shouldNotifyScan(previousScanAt: Date | null, scanAt: Date): boolean {
  if (!previousScanAt) return true;
  return todayKey(previousScanAt, CENTRAL_TZ) !== todayKey(scanAt, CENTRAL_TZ);
}

/** The last n Central dates, oldest first, ending today. */
export function lastNDayKeys(now: Date, n: number): string[] {
  const today = todayKey(now, CENTRAL_TZ);
  return Array.from({ length: n }, (_, i) => addDaysKey(today, -(n - 1 - i)));
}

export function fillDays(keys: string[], rows: { day: string; n: number }[]): number[] {
  const byDay = new Map<string, number>();
  for (const r of rows) byDay.set(r.day, (byDay.get(r.day) ?? 0) + r.n);
  return keys.map((k) => byDay.get(k) ?? 0);
}

/** One-click destinations in the create dialog. Paths verified against app/(site) in the website repo. */
export const QUICK_DESTINATIONS: { label: string; path: string }[] = [
  { label: "Home", path: "/" },
  { label: "Valuation calculator", path: "/business-valuation-calculator" },
  { label: "Contact", path: "/contact" },
  { label: "Buyer network", path: "/buyer-network" },
];

/** For lists: our own pages read as a path ("/contact"), anything else as host + path. */
export function shortDestination(url: string): string {
  try {
    const u = new URL(url);
    if (u.origin === WEBSITE_ORIGIN) return u.pathname;
    return u.host + (u.pathname === "/" ? "" : u.pathname);
  } catch {
    return url;
  }
}
